import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams, NavLink } from 'react-router-dom';
import { useAuth } from 'model/Auth';
import { useApi } from 'model/Api';
import { User } from 'model/User';
import { hasPermission } from 'model/Route';
import { generalStyle } from 'helper/Style';
import { convertUserName } from 'helper/Converter';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import FormContent from 'component/part/FormContent';
import Modal, { modalStyle } from 'component/part/Modal';
import H2 from 'component/part/H2';

const MemberShow: React.FC = () => {
  const api = useApi();
  const { user: owner } = useAuth();
  const enableEdit = owner && hasPermission('member', 'edit', owner.role);
  const displaySensitive = !!owner?.role?.permissions.userEdit;
  const navigate = useNavigate();
  const urlParams = useParams<{ id: string }>();
  const userId = parseInt(urlParams.id || '');
  const [user, setUser] = useState<User>();
  const [confirm, setConfirm] = useState(false);
  const [resultStatus, setResultStatus] = useState(0);
  const [email, accountName] = user?.role?.permissions.login
    ? [user?.email, user?.accountName]
    : [null, null];

  const onSubmit = async () => {
    if (user) {
      const result = await api.user.delete(user.id);
      setConfirm(false);
      setResultStatus(result ? 1 : 2);
    }
  };

  const onUnderstoodResult = () => {
    setResultStatus(0);
    if (resultStatus === 1) {
      navigate('/member/list');
    }
  };

  useEffect(() => {
    (async () => {
      if (userId) {
        const userResponse = await api.user.get(userId, { with: ['role'] });
        if (userResponse) {
          setUser(userResponse);
        }
      }
    })();
  }, [userId]);

  return <Layout>
    <BackLink link='/member/list' label='メンバー一覧'></BackLink>
    <H2>メンバー情報</H2>
    <div css={generalStyle.form}>
      <FormContent label='氏名'>
        <div css={generalStyle.item()}>{user ? convertUserName(user) : ''}</div>
      </FormContent>
      <FormContent label='役職'>
        <div css={generalStyle.item()}>{user?.role?.name || ''}</div>
      </FormContent>
      {displaySensitive && (
        <Fragment>
          <FormContent label='ユーザー名'>
            <div css={generalStyle.item()}>{accountName || 'ー'}</div>
          </FormContent>
          <FormContent label='メールアドレス'>
            <div css={generalStyle.item()}>{email || 'ー'}</div>
          </FormContent>
          <FormContent label='日給'>
            <div css={generalStyle.item()}>{user?.salary ? `\xA5${user.salary.toLocaleString()}` : 'ー'}</div>
          </FormContent>
        </Fragment>
      )}
      {user && enableEdit && (
        <div css={generalStyle.formRow}>
          <NavLink to={`/member/${user.id}/edit`} css={generalStyle.button()}>編集</NavLink>
          <button onClick={() => setConfirm(true)} css={generalStyle.button('warning')}>削除</button>
        </div>
      )}
    </div>
    <Modal display={confirm} onHide={() => setConfirm(false)}>
      <div>このメンバーを削除します</div>
      <div css={modalStyle.buttonArea}>
        <button onClick={onSubmit} css={generalStyle.button()}>確定</button>
        <button onClick={() => setConfirm(false)} css={generalStyle.button('warning')}>キャンセル</button>
      </div>
    </Modal>
    <Modal display={!!resultStatus} onHide={onUnderstoodResult}>
      <div>
        {resultStatus === 1 ? '削除が完了しました' : '削除に失敗しました'}
      </div>
      <div css={modalStyle.buttonArea}>
        <button onClick={onUnderstoodResult} css={generalStyle.button()}>OK</button>
      </div>
    </Modal>
  </Layout>;
};

export default MemberShow;
